import React, { useState, useMemo, useCallback } from 'react';
import { GraduationCap, Sparkles } from 'lucide-react';
import { CoursesFilters } from '@/hooks/queries/useCourses';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from '@/hooks/useTranslation';
import CoursesFiltersBar from '@/components/courses/CoursesFiltersBar';
import CoursesContent from '@/components/courses/CoursesContent';
import PageBackground from '@/components/common/PageBackground';
import { NestedCategoryFilter } from '@/components/base';

const CoursesPage: React.FC = () => {
  const { t } = useTranslation('courses');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');

  const filters: CoursesFilters = useMemo(
    () => ({
      limit: 12,
      language: selectedLanguage === 'all' ? 'en' : selectedLanguage,
      categoryIds: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
      isPublished: true, // Only show published courses for user-facing page
      search: debouncedSearch || undefined,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      // TODO: remove when we sort out publishing changes of a course
      useDraftData: true,
    }),
    [selectedCategoryIds, selectedLanguage, debouncedSearch],
  );

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCategoryIds([]);
    setSelectedLanguage('all');
  }, []);

  const hasActiveFilters = !!(
    searchQuery ||
    selectedCategoryIds.length > 0 ||
    (selectedLanguage && selectedLanguage !== 'all')
  );

  return (
    <PageBackground>
      {/* Modern Header */}
      <div className="mb-12 text-center sm:text-left">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50" />
            <div className="relative p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{t('subtitle')}</p>
            </div>
          </div>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mt-4 leading-relaxed">{t('description')}</p>
      </div>

      {/* Filters */}
      <CoursesFiltersBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Main Content with Category Sidebar */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Category Sidebar */}
        <aside className="lg:w-72 flex-shrink-0">
          <div className="lg:sticky lg:top-4">
            <NestedCategoryFilter selectedCategoryIds={selectedCategoryIds} onChange={setSelectedCategoryIds} />
          </div>
        </aside>

        {/* Courses Content */}
        <CoursesContent
          filters={filters}
          selectedLanguage={selectedLanguage}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />
      </div>
    </PageBackground>
  );
};

export default CoursesPage;
